import { t } from "@/lib/i18n/interpolate";
import type { DocBlock } from "@/lib/tool-kit/document";
import { allocateWords } from "@/lib/tool-kit/essay-templates/research-proposal";
import { parseWholeCount } from "@/lib/tool-kit/essay-templates/text";

/**
 * Word budgets for the business-writing templates: a total length split across sections
 * by a share table, with targets that add up exactly (largest-remainder rounding, the
 * same allocator the research-proposal tool uses).
 */

export type Format = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const PERCENT: Intl.NumberFormatOptions = { style: "percent" };

/** A typed length in words, or null when it is blank or outside `min`–`max`. */
export function wordTotal(
  raw: string,
  min: number,
  max: number
): number | null {
  const total = parseWholeCount(raw);
  if (total === null || total < min || total > max) {
    return null;
  }
  return total;
}

/**
 * Targets for each key, rounded to `step` words, that sum to the total rounded to
 * `step`. A key whose share is 0 gets 0. Null when there is no usable total.
 */
export function wordTargets<K extends string>(
  keys: readonly K[],
  shares: Record<K, number>,
  total: number | null,
  step = 10
): Record<K, number> | null {
  if (total === null) {
    return null;
  }
  const allocated = allocateWords(
    total,
    keys.map((key) => shares[key]),
    step
  );
  return Object.fromEntries(
    keys.map((key, index) => [key, allocated[index]])
  ) as Record<K, number>;
}

export function sharePercent(fmt: Format, share: number): string {
  return fmt(share / 100, PERCENT);
}

export interface BudgetLabels {
  section: string;
  share: string;
  words: string;
  total: string;
}

/** The budget table: section, share, and — when a total is set — a word target. */
export function budgetTable<K extends string>(
  keys: readonly K[],
  names: Record<K, string>,
  shares: Record<K, number>,
  targets: Record<K, number> | null,
  labels: BudgetLabels,
  fmt: Format
): DocBlock {
  const counted = keys.filter((key) => shares[key] > 0);
  const rows = counted.map((key) => {
    const row = [names[key], sharePercent(fmt, shares[key])];
    return targets ? [...row, fmt(targets[key])] : row;
  });
  const sum = targets
    ? counted.reduce((total, key) => total + targets[key], 0)
    : 0;
  const totalRow = [labels.total, fmt(1, PERCENT)];
  const head = [labels.section, labels.share];
  return {
    kind: "table",
    head: targets ? [...head, labels.words] : head,
    rows: [...rows, targets ? [...totalRow, fmt(sum)] : totalRow],
  };
}

/** "Target: 450 words (15%). Tip…" — or just the share while no total is set. */
export function targetLine(
  share: number,
  target: number | null,
  templates: { withWords: string; shareOnly: string },
  fmt: Format
): string {
  return target === null
    ? t(templates.shareOnly, { share: sharePercent(fmt, share) })
    : t(templates.withWords, {
        words: fmt(target),
        share: sharePercent(fmt, share),
      });
}
